import { useEffect, useMemo, useRef, useState } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import { AppDialog } from '../Common/AppDialog'
import { Button, Pressable } from '../Common'
import { MemberActionCard, MemberBottomSheet } from './controls/MemberControlsPrimitives'
import { MemberAiAgentTab } from './MemberAiAgentTab'
import { MemberAiHistoryTab } from './MemberAiHistoryTab'
import { liveAiHistorySessions } from './MemberAiHistoryTab.helpers'
import { MemberAiPulseTab } from './MemberAiPulseTab'
import { MemberPulseProvider } from '../../context/MemberPulseContext'
import { useMemberLayout } from './MemberLayoutContext'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { useWorkspaceWalletBalance } from '../../hooks/useCreditBalances'
import { CreditBalance } from '../Credits/CreditBalance'
import { formatCredits as formatCreditsShared } from '../Credits/formatCredits'
import { AI_AGENT_COSTS, createAITreeProposal } from '../../services/aiAgentBridge'
import { subscribeAiChatSessions, type AiChatSession } from '../../services/aiChatHistoryService'
import { createAIStatusProposal, executeMemberAIProposal } from '../../services/memberControlsService'
import { createDeltaOperationId } from '../../services/stripboardDeltaService'
import { isNoSignInPreviewActive } from '../../debug'
import { testingOverrideAllowsAllFeatures } from '../../config/testingOverrides'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import type { TimelineElement } from '../../types/timelineElement'
import type { MemberAiSubTab } from '../../member/memberAppRouting'

type AiMode = 'CREATE' | 'EXECUTE'
type AiSection = MemberAiSubTab
type PendingAction = {
  id: 'STATUS_DELTA' | 'TREE_CREATE'
  title: string
  description: string
  cost: number
  run: () => Promise<void>
}

const nextStatusFor = (element: TimelineElement) => {
  const current = element.boardStatus ?? element.workflowStatus ?? 'TODO'
  if (current === 'TODO') return 'IN_PROGRESS'
  if (current === 'IN_PROGRESS') return 'COMPLETE'
  return 'TODO'
}

const EMPTY_HISTORY_SESSIONS: AiChatSession[] = []

export const AiMagicController = ({
  role,
  mode: controlledMode,
  section,
  onRequestSection,
  onOpenBoardDashboard,
  chrome = 'desktop',
}: {
  role: WorkspaceRole
  /** When provided, the mode is controlled from outside (member mobile
   * secondary tabs) and the internal segmented switch is hidden. */
  mode?: AiMode
  section?: AiSection
  onRequestSection?: (section: MemberAiSubTab) => void
  onOpenBoardDashboard?: () => void
  /** 'mobile' drops the Controls-style header and applies the desktop
   * AI Magic theme adapted for the member mobile shell. */
  chrome?: 'desktop' | 'mobile'
}) => {
  const { workspaceId, elements } = useTimeline()
  const { activeWorkspaceId } = useWorkspace()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const wallet = useWorkspaceWalletBalance(activeWorkspaceId, isNoSignInPreviewActive() ? 5000 : 0, isNoSignInPreviewActive())
  const [internalMode, setMode] = useState<AiMode>('CREATE')
  const mode = controlledMode ?? internalMode
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [runningActionId, setRunningActionId] = useState<string | null>(null)
  const [historySessions, setHistorySessions] = useState<AiChatSession[]>([])
  const [openSessionRequest, setOpenSessionRequest] = useState<{ session: AiChatSession; nonce: number } | null>(null)
  const [newChatNonce, setNewChatNonce] = useState(0)
  const [creditsSheetOpen, setCreditsSheetOpen] = useState(false)
  const canUseAi = roleCanPerform(role, 'EDITOR') || testingOverrideAllowsAllFeatures()
  const effectiveWorkspaceId = activeWorkspaceId ?? workspaceId
  const memberLayout = useMemberLayout()
  const isInitialSubMount = useRef(true)
  const [prevSection, setPrevSection] = useState(section)
  const [lastSection, setLastSection] = useState<AiSection | undefined>(undefined)

  if (section !== prevSection) {
    setPrevSection(section)
    setLastSection(prevSection)
  }

  useEffect(() => {
    if (isInitialSubMount.current) {
      isInitialSubMount.current = false
      return
    }
    memberLayout?.scrollStageToTop()
  }, [section, memberLayout])

  const candidate = useMemo(() => elements.find((element) => (
    (element.type === 'TASK' || element.type === 'SUBTASK')
    && element.workspaceId === effectiveWorkspaceId
    && element.board === 'DASHBOARD'
  )), [effectiveWorkspaceId, elements])

  const executeStatusDelta = async () => {
    if (!effectiveWorkspaceId || !candidate) throw new Error('No movable task or subtask is available.')
    const destinationStatus = nextStatusFor(candidate)
    const proposal = await createAIStatusProposal({
      workspaceId: effectiveWorkspaceId,
      title: `Move ${candidate.title} to ${destinationStatus.replaceAll('_', ' ')}`,
      creditCost: AI_AGENT_COSTS.BIG_IDEA,
      delta: {
        operationId: createDeltaOperationId('member-ai-status', candidate.id),
        operationType: 'STATUS_MOVE',
        recordId: candidate.id,
        recordKind: candidate.type,
        sourceStatus: candidate.boardStatus ?? null,
        destinationStatus,
        expectedVersion: candidate.recordVersion ?? 1,
      },
    })
    await executeMemberAIProposal(proposal.proposalId)
  }

  const executeTreeCreate = async () => {
    if (!effectiveWorkspaceId) throw new Error('Workspace is not ready.')
    const proposal = await createAITreeProposal({
      workspaceId: effectiveWorkspaceId,
      title: 'Member field follow-up plan',
      board: 'DASHBOARD',
      creditCost: AI_AGENT_COSTS.BIG_IDEA,
      tree: {
        title: 'Member field follow-up',
        description: 'AI-created execution plan from Member Controls.',
        tasks: [
          {
            title: 'Collect field updates',
            description: 'Gather outstanding updates from the team.',
            subTasks: [{ title: 'Summarize blockers', description: 'Identify top operational blockers.' }],
            checklist: [{ title: 'Confirm each member has submitted an update' }],
          },
        ],
        checklist: [{ title: 'Review the AI-created plan before assigning work' }],
      },
    })
    await executeMemberAIProposal(proposal.proposalId)
  }

  const cards: PendingAction[] = [
    {
      id: 'STATUS_DELTA',
      title: t('NLSAiMagicStatusDeltaTitle'),
      description: candidate ? t('NLSAiMagicStatusDeltaDescription', { task: candidate.title }) : t('NLSAiMagicStatusDeltaEmptyDescription'),
      cost: AI_AGENT_COSTS.BIG_IDEA,
      run: executeStatusDelta,
    },
    {
      id: 'TREE_CREATE',
      title: 'Create follow-up plan',
      description: 'Generate a small Dashboard execution tree through the AI proposal bridge.',
      cost: AI_AGENT_COSTS.BIG_IDEA,
      run: executeTreeCreate,
    },
  ]

  useEffect(() => {
    if (!effectiveWorkspaceId || !currentUser?.uid) {
      return undefined
    }
    const unsub = subscribeAiChatSessions(effectiveWorkspaceId, currentUser.uid, setHistorySessions)
    return () => {
      unsub()
      setHistorySessions([])
    }
  }, [currentUser?.uid, effectiveWorkspaceId])

  const effectiveHistorySessions = !effectiveWorkspaceId || !currentUser?.uid ? EMPTY_HISTORY_SESSIONS : historySessions

  const approve = async () => {
    if (!pendingAction) return
    setRunningActionId(pendingAction.id)
    try {
      await pendingAction.run()
      showToast(`${pendingAction.title} executed.`, 'success')
      setPendingAction(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'AI action failed.', 'error')
    } finally {
      setRunningActionId(null)
    }
  }

  const liveHistorySessions = useMemo(() => liveAiHistorySessions(effectiveHistorySessions), [effectiveHistorySessions])
  const historyCountFor = (nextMode: AiMode) =>
    liveHistorySessions.filter((session) => session.operatingMode === nextMode).length

  const renderCreditsSheet = () => (
    <MemberBottomSheet
      open={creditsSheetOpen}
      title="Workspace credits"
      onClose={() => setCreditsSheetOpen(false)}
      testId="member-ai-credits-sheet"
    >
      <div className="memberAiCreditsSheet">
        <dl>
          <div><dt>Available balance</dt><dd><CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} /></dd></div>
          <div><dt>Workspace scope</dt><dd>Shared AI Magic balance</dd></div>
        </dl>
        <Button variant="primary" size="lg" onClick={() => showToast('Credit top-up opens from workspace billing.', 'ghost')}>Top up credits</Button>
      </div>
    </MemberBottomSheet>
  )

  const renderMobileApproveSheet = () => (
    <MemberBottomSheet
      open={Boolean(pendingAction)}
      title="Approve AI execution"
      onClose={() => runningActionId ? undefined : setPendingAction(null)}
      testId="member-ai-approve-sheet"
    >
      {pendingAction ? (
        <div className="memberAutomationApprove memberAiApproveSheet">
          <div className="memberConfirmStack">
            <p>{pendingAction.description}</p>
            <dl>
              <div><dt>Estimated cost</dt><dd>{formatCreditsShared(pendingAction.cost, { suffix: 'credits' })}</dd></div>
              <div><dt>Wallet balance</dt><dd><CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} /></dd></div>
            </dl>
          </div>
          <div className="memberAutomationApprove__actions">
            <Button variant="secondary" size="lg" onClick={() => setPendingAction(null)} disabled={runningActionId !== null}>Cancel</Button>
            <Button variant="primary" size="lg" onClick={() => void approve()} disabled={runningActionId !== null}>
              {runningActionId ? 'Executing...' : 'Approve'}
            </Button>
          </div>
        </div>
      ) : null}
    </MemberBottomSheet>
  )

  const executeQuickActions = (
    <div className="memberAiExecuteQuickActions" aria-label="Execute quick actions">
      {cards.map((card) => (
        <MemberActionCard
          key={card.id}
          icon={<BoltRoundedIcon fontSize="inherit" />}
          title={card.title}
          sub={card.description}
          status={`${card.cost} cr`}
          statusTone="info"
          disabled={runningActionId !== null || (card.id === 'STATUS_DELTA' && !candidate)}
          onClick={() => setPendingAction(card)}
        />
      ))}
    </div>
  )

  const renderMobileSection = () => {
    if (!canUseAi) {
      return <div className="memberControlEmpty"><LockRoundedIcon /><strong>Read only</strong><span>AI Magic is available to Admins and Editors.</span></div>
    }
    if (section === 'MAGIC') {
      return (
        <div className="memberControlPanel memberAiMagicPanel memberAiMagicShell" data-testid="member-ai-magic-subtab">
          <header className="memberAiMagicHero">
            <span className="memberAiMagicHeroGlyph" aria-hidden="true"><AutoAwesomeRoundedIcon fontSize="inherit" /></span>
            <div>
              <span className="memberEyebrow">AI Magic</span>
              <h2>Brain controls</h2>
            </div>
          </header>
          <div className="memberControlGrid">
            <article className="memberControlCard">
              <AutoAwesomeRoundedIcon />
              <strong>Field follow-up plan</strong>
              <p>Create mode uses the server-owned AI proposal bridge. Switch to Execute to approve the credit-consuming action.</p>
            </article>
            <article className="memberControlCard">
              <CheckCircleRoundedIcon />
              <strong>Wallet gated</strong>
              <p>Available workspace credits: {formatCreditsShared(wallet.availableCredits, { suffix: 'none' })}</p>
            </article>
          </div>
          <div className="memberControlGrid">
            {cards.map((card) => (
              <Pressable
                key={card.id}
                className="memberExecuteCard"
                disabled={runningActionId !== null || (card.id === 'STATUS_DELTA' && !candidate)}
                onClick={() => setPendingAction(card)}
              >
                <BoltRoundedIcon />
                <span>
                  <strong>{card.title}</strong>
                  <small>{card.description}</small>
                </span>
                <em>{card.cost} cr</em>
              </Pressable>
            ))}
          </div>
          {renderMobileApproveSheet()}
        </div>
      )
    }
    if (section === 'HISTORY') {
      const initialFilter = lastSection === 'EXECUTE' ? 'EXECUTE' : 'CREATE'
      return (
        <MemberAiHistoryTab
          sessions={effectiveHistorySessions}
          activeSessionId={openSessionRequest?.session.id ?? null}
          initialFilter={initialFilter}
          walletCredits={wallet.availableCredits}
          onWalletClick={() => setCreditsSheetOpen(true)}
          onOpenSession={(session) => {
            const nextSection = session.operatingMode === 'EXECUTE' ? 'EXECUTE' : 'CREATE'
            onRequestSection?.(nextSection)
            setOpenSessionRequest({ session, nonce: Date.now() })
          }}
          onNewSession={(nextMode) => {
            setOpenSessionRequest(null)
            setNewChatNonce((current) => current + 1)
            onRequestSection?.(nextMode)
          }}
        />
      )
    }
    if (section === 'PULSE') {
      return effectiveWorkspaceId && currentUser?.uid
        ? <MemberAiPulseTab workspaceId={effectiveWorkspaceId} uid={currentUser.uid} walletCredits={wallet.availableCredits} onOpenBoardDashboard={onOpenBoardDashboard} onWalletClick={() => setCreditsSheetOpen(true)} />
        : <div className="memberControlEmpty"><strong>Workspace is loading</strong></div>
    }
    if (section === 'CREATE') {
      return effectiveWorkspaceId ? (
        <MemberAiAgentTab
          key={`CREATE-${openSessionRequest?.nonce ?? `new-${newChatNonce}`}`}
          walletCredits={wallet.availableCredits}
          openSessionRequest={openSessionRequest}
          lockedMode="CREATE"
          historyCount={historyCountFor('CREATE')}
          onOpenHistory={() => onRequestSection?.('HISTORY')}
          onWalletClick={() => setCreditsSheetOpen(true)}
          onNewChatStarted={() => setOpenSessionRequest(null)}
        />
      ) : <div className="memberControlEmpty"><strong>Workspace is loading</strong></div>
    }
    return effectiveWorkspaceId ? (
      <MemberAiAgentTab
        key={`EXECUTE-${openSessionRequest?.nonce ?? `new-${newChatNonce}`}`}
        walletCredits={wallet.availableCredits}
        openSessionRequest={openSessionRequest}
        lockedMode="EXECUTE"
        quickActions={executeQuickActions}
        historyCount={historyCountFor('EXECUTE')}
        onOpenHistory={() => onRequestSection?.('HISTORY')}
        onWalletClick={() => setCreditsSheetOpen(true)}
        onNewChatStarted={() => setOpenSessionRequest(null)}
      />
    ) : <div className="memberControlEmpty"><strong>Workspace is loading</strong></div>
  }

  if (chrome === 'mobile' && section) {
    return (
      <MemberPulseProvider
        workspaceId={effectiveWorkspaceId ?? ''}
        uid={currentUser?.uid ?? ''}
        walletCredits={wallet.availableCredits}
      >
        <section className="memberControlPanel memberAiMagicPanel memberAiMagicShell" data-testid="member-ai-controller">
          {renderMobileSection()}
          {renderMobileApproveSheet()}
          {renderCreditsSheet()}
        </section>
      </MemberPulseProvider>
    )
  }

  return (
    <section
      className={chrome === 'mobile' ? 'memberControlPanel memberAiMagicPanel memberAiMagicShell' : 'memberControlPanel'}
      data-testid="member-ai-controller"
    >
      {chrome === 'desktop' ? (
        <header className="memberControlHeader">
          <div>
            <span className="memberEyebrow">AI Magic</span>
            <h2>Brain controls</h2>
          </div>
          {controlledMode === undefined ? (
            <div className="memberSegmented" role="tablist" aria-label="AI mode">
              {(['CREATE', 'EXECUTE'] as AiMode[]).map((entry) => (
                <Pressable key={entry} aria-pressed={mode === entry} className={mode === entry ? 'isActive' : ''} onClick={() => setMode(entry)}>{entry === 'CREATE' ? 'Create' : 'Execute'}</Pressable>
              ))}
            </div>
          ) : null}
        </header>
      ) : (
        <header className="memberAiMagicHero">
          <span className="memberAiMagicHeroGlyph" aria-hidden="true">{mode === 'CREATE' ? <AutoAwesomeRoundedIcon fontSize="inherit" /> : <MapRoundedIcon fontSize="inherit" />}</span>
          <div>
            <span className="memberEyebrow">AI Magic</span>
            <h2>{mode === 'CREATE' ? 'Generate' : 'Execute'}</h2>
          </div>
        </header>
      )}
      {!canUseAi ? (
        <div className="memberControlEmpty"><LockRoundedIcon /><strong>Read only</strong><span>AI Magic is available to Admins and Editors.</span></div>
      ) : mode === 'CREATE' ? (
        <div className="memberControlGrid">
          <article className="memberControlCard">
            <AutoAwesomeRoundedIcon />
            <strong>Field follow-up plan</strong>
            <p>Create mode uses the server-owned AI proposal bridge. Switch to Execute to approve the credit-consuming action.</p>
          </article>
          <article className="memberControlCard">
            <CheckCircleRoundedIcon />
            <strong>Wallet gated</strong>
            <p>Available workspace credits: {formatCreditsShared(wallet.availableCredits, { suffix: 'none' })}</p>
          </article>
        </div>
      ) : (
        <div className="memberControlGrid">
          {cards.map((card) => (
            <Pressable
              key={card.id}
              className="memberExecuteCard"
              disabled={runningActionId !== null || (card.id === 'STATUS_DELTA' && !candidate)}
              onClick={() => setPendingAction(card)}
            >
              <BoltRoundedIcon />
              <span>
                <strong>{card.title}</strong>
                <small>{card.description}</small>
              </span>
              <em>{card.cost} cr</em>
            </Pressable>
          ))}
        </div>
      )}
      <AppDialog
        open={Boolean(pendingAction)}
        title="Approve AI execution"
        message={pendingAction ? (
          <div className="memberConfirmStack">
            <p>{pendingAction.description}</p>
            <dl>
              <div><dt>Estimated cost</dt><dd>{formatCreditsShared(pendingAction.cost, { suffix: 'credits' })}</dd></div>
              <div><dt>Wallet balance</dt><dd><CreditBalance value={wallet.availableCredits} variant="bare" scope="workspace" label={null} /> credits</dd></div>
            </dl>
          </div>
        ) : null}
        onDismiss={() => runningActionId ? undefined : setPendingAction(null)}
        actions={[
          { label: 'Cancel', onClick: () => setPendingAction(null), disabled: runningActionId !== null },
          { label: runningActionId ? 'Executing...' : 'Approve', variant: 'primary', autoFocus: true, disabled: runningActionId !== null, loading: runningActionId !== null, onClick: approve },
        ]}
      />
    </section>
  )
}
